import React, { useEffect, useState, useRef } from 'react';
import { NavigationContainer, LinkingOptions, CommonActions } from '@react-navigation/native';
import { clarityHelpers } from '../utils/clarity';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { Platform, TouchableOpacity, View, Text, ActivityIndicator } from 'react-native';
import { Ionicons, MaterialIcons } from '@expo/vector-icons';
import { useAppDispatch, useAppSelector } from '../store/store';
import {
  loadUserProfile,
  logoutUser,
  selectIsAuthenticated,
  selectAuthLoading,
  selectUser,
  selectRequiresVerification,
  selectVerificationToken,
} from '../features/auth/authSlice';
import { FeedbackProvider, FeedbackModal } from '../components';
import { ShareModal } from '../features/sharing/components/ShareModal';
import { CommandPalette } from '../components/CommandPalette';
import { colors, fontSizes, spacing } from '../styles/theme';

// Import all screens
import { LoginScreen } from '../features/auth/LoginScreen';
import { RegisterScreen } from '../features/auth/RegisterScreen';
import { EmailVerificationScreen } from '../features/auth/EmailVerificationScreen';
import { ForgotPasswordScreen } from '../features/auth/ForgotPasswordScreen';
import { ResetPasswordScreen } from '../features/auth/ResetPasswordScreen';
import { DashboardScreen } from '../features/dashboard/DashboardScreen';
import { BrowseScreen } from '../features/browse/BrowseScreen';
import { CrewHubScreen } from '../features/sharing/CrewHubScreen';
import { ShareFlowScreen } from '../features/sharing/ShareFlowScreen';
import { ProfileScreen } from '../features/auth/ProfileScreen';
import { EditProfileScreen } from '../features/auth/EditProfileScreen';
import { ChangePasswordScreen } from '../features/auth/ChangePasswordScreen';
import { ChangeEmailScreen } from '../features/auth/ChangeEmailScreen';
import { ShareClaimScreen } from '../features/sharing/ShareClaimScreen';
import { ItemDetailScreen } from '../features/inventory/ItemDetailScreen';
import { UploadScreen } from '../features/upload/UploadScreen';
import { SegmentScreen } from '../features/segment/SegmentScreen';
// FE #259 — Founsi AI chat tab (5th, middle slot).
import { FounsiScreen } from '../features/founsi/FounsiScreen';

/**
 * Feature flag for the segmentation UX v2 (Chunks 2–7).
 *
 * Default: ENABLED on web. Two opt-out levers if rollout sees a
 * regression and we need to revert without code changes:
 *   - Build-time kill-switch: `EXPO_PUBLIC_SEGMENTATION_UI=0` (or
 *     `false`) in the build env → falls back to the legacy upload
 *     flow.
 *   - Runtime kill-switch: `localStorage.setItem('segmentation_ui',
 *     '0')` in the browser console → same effect for one client,
 *     no rebuild needed.
 *
 * Native always gets the legacy flow regardless because the
 * streaming client (EventSource / SSE polyfill) throws on native;
 * the v2 UI requires it.
 */
const isSegmentationUiEnabled = (): boolean => {
  if (Platform.OS !== 'web') return false;
  // Build-time opt-out
  if (
    process.env.EXPO_PUBLIC_SEGMENTATION_UI === '0' ||
    process.env.EXPO_PUBLIC_SEGMENTATION_UI === 'false'
  ) {
    return false;
  }
  // Runtime opt-out
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const lsFlag = window.localStorage.getItem('segmentation_ui');
      if (lsFlag === '0' || lsFlag === 'false') return false;
    }
  } catch {
    /* private-mode / storage-unavailable — fall through to default */
  }
  return true;
};

// Evaluated once per bundle mount. Changing localStorage requires
// a page reload — that's fine for QA + we keep the nav tree stable
// for hot-reload friendliness.
const SEGMENTATION_UI_ENABLED = isSegmentationUiEnabled();

const ScanRouteComponent = SEGMENTATION_UI_ENABLED
  ? SegmentScreen
  : UploadScreen;
import { FeedbackScreen } from '../features/feedback/FeedbackScreen';
import { CreateGroupScreen } from '../features/sharing/CreateGroupScreen';
import { GroupDetailScreen } from '../features/sharing/GroupDetailScreen';
import { AcceptInviteScreen } from '../features/sharing/AcceptInviteScreen';
import { CrossGroupSearchScreen } from '../features/search/CrossGroupSearchScreen';
import { SceneViewerScreen } from '../features/browse/SceneViewerScreen';
import {
  getPendingInviteToken,
  clearPendingInviteToken,
} from '../features/sharing/pendingInvite';
import { consumePostBootRedirect } from './deepLinkRedirect';
import { SharingRequestsScreen } from '../features/sharing/SharingRequestsScreen';
import { MySharesScreen } from '../features/sharing/MySharesScreen';
import { MyCustodyScreen } from '../features/sharing/MyCustodyScreen';
import { loadGroups, cleanupLegacyMockStorage } from '../features/sharing/groupsSlice';
import { hydrateItemExtras } from '../features/inventory/itemExtrasSlice';
import { NotificationsScreen } from '../features/notifications/NotificationsScreen';
// FE #124 — Scan History page (USJ.md J2.B4 v2)
import { ScanHistoryScreen } from '../features/scans/ScanHistoryScreen';
import { fpColors } from '../styles/theme';

const Stack = createNativeStackNavigator();
const Tab = createBottomTabNavigator();

// Deep linking configuration - simplified and more reliable
const linking: LinkingOptions<any> = {
  prefixes: [
    'https://app.remembr.xyz',
    'https://remembr.xyz',
    'https://founsi.ai',
    'https://app.founsi.ai',
  ],
  config: {
    screens: {
      // Share + Invite links — work for BOTH authenticated and
      // unauthenticated users. AcceptInvite is registered in BOTH
      // stacks (auth + unauth) — this top-level pattern makes the
      // URL match regardless of auth state. (Sprint 2.5 / FE-9.4.5
      // bug fix — was previously only matchable inside MainApp.)
      ShareClaim: {
        path: 'share/:shareToken',
        parse: {
          shareToken: (shareToken: string) => shareToken,
        },
      },
      AcceptInvite: {
        path: 'invite/:token',
        parse: {
          token: (token: string) => token,
        },
      },

      // Auth screens
      Login: 'login',
      Register: 'register',
      ForgotPassword: 'forgot-password',
      ResetPassword: 'reset-password',
      EmailVerification: 'verify-email',

      // Main app routes - all require authentication
      MainApp: {
        screens: {
          MainTabs: {
            screens: {
              Home: 'home',
              Browse: 'browse',
              Community: 'community',
            },
          },
          Profile: 'profile',
          EditProfile: 'edit-profile',
          ChangePassword: 'change-password',
          ChangeEmail: 'change-email',
          Upload: 'upload',
          Feedback: 'feedback',
          ItemDetail: 'item/:itemId',
          CreateGroup: 'create-group',
          GroupDetail: 'groups/:groupId',
          // NOTE: AcceptInvite intentionally NOT given a URL pattern
          // here. React Navigation rejects duplicate patterns —
          // 'invite/:token' is registered top-level (works for both
          // auth + unauth users). The Stack.Screen inside
          // MainAppNavigator is still mounted for programmatic
          // navigation (from the post-auth boot-resume hook); it
          // just doesn't compete for the URL pattern.
          SharingRequests: 'sharing/requests',
          // Sprint 3 (FE-11.5.E) — Scene-detail viewer.
          SceneViewer: 'scenes/:fileId',
          // Sprint 8 / FE-128 — In-app notification inbox.
          Notifications: 'notifications',
          // Sprint 8 Phase C — aggregate share/custody deep-dives.
          MyShares: 'my-shares',
          MyCustody: 'my-custody',
          // FE #124 — Scan history page (USJ.md J2.B4 v2).
          //  Entered via:
          //  - Dashboard recovery banner [View all N →]
          //  - [+ Add New Item] sheet "Resume scan" row
          //  - Top-bar 🕒 clock-rewind icon (always visible)
          //  - Direct deep-link /scans
          ScanHistory: 'scans',
        },
      },

      // Fallback
      NotFound: '*',
    },
  },
};

// Header component with profile and feedback buttons
const HeaderButtons = ({ navigation }: { navigation: any }) => {
  const user = useAppSelector(selectUser);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const getInitials = (fullName: string | undefined): string => {
    if (!fullName) return '?';
    const names = fullName.split(' ');
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  };

  return (
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
      {/* Feedback Button */}
      <TouchableOpacity
        style={{ padding: 8, marginRight: 6 }}
        onPress={() => setIsModalVisible(true)}
        accessibilityLabel="Send feedback"
      >
        <MaterialIcons name="chat-bubble-outline" size={20} color={fpColors.onSurface} />
      </TouchableOpacity>

      {/* Profile Button */}
      <TouchableOpacity
        style={{ padding: 6, marginRight: 8 }}
        onPress={() => navigation.navigate('Profile')}
        accessibilityLabel="Profile"
      >
        <View
          style={{
            width: 32,
            height: 32,
            borderRadius: 16,
            backgroundColor: fpColors.primaryContainer,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Text
            style={{
              fontFamily: 'Inter_700Bold',
              fontSize: 12,
              color: fpColors.onPrimaryContainer,
              letterSpacing: 0.5,
            }}
          >
            {getInitials(user?.full_name)}
          </Text>
          {user?.is_verified && (
            <View
              style={{
                position: 'absolute',
                bottom: -2,
                right: -2,
                width: 14,
                height: 14,
                borderRadius: 7,
                backgroundColor: fpColors.primary,
                justifyContent: 'center',
                alignItems: 'center',
                borderWidth: 1.5,
                borderColor: fpColors.surfaceContainerLowest,
              }}
            >
              <MaterialIcons name="check" size={8} color="#fff" />
            </View>
          )}
        </View>
      </TouchableOpacity>

      {/* Feedback Modal */}
      <FeedbackModal visible={isModalVisible} onClose={() => setIsModalVisible(false)} />
    </View>
  );
};

// Main tabs navigator for authenticated users
const MainTabs = () => {
  return (
    <Tab.Navigator
      initialRouteName="Home"
      screenOptions={({ route, navigation }: { route: any; navigation: any }) => ({
        tabBarIcon: ({ focused, color, size }: { focused: boolean; color: string; size: number }) => {
          // Map route → MaterialIcons glyph (filled when focused to match
          // Stitch). MaterialIcons doesn't expose a per-glyph "filled"
          // variant, so we approximate by keeping the same glyph and
          // leaning on color + background for focus.
          // Sprint 8 follow-up — 4-tab restructure:
          //   Home (Dashboard) · Browse (Items) · ShareFlow · Community (Crews)
          // Verify tab dropped (PR1). Community renamed Crews and slimmed
          // to people-only (PR3). ShareFlow holds the unified item-flow
          // surface (PR4: With me / Out from me / My borrow asks).
          const glyphMap: Record<string, keyof typeof MaterialIcons.glyphMap> = {
            Home: 'dashboard',
            Browse: 'list-alt',
            Founsi: 'auto-awesome',
            ShareFlow: 'swap-horiz',
            Community: 'groups',
          };
          const glyph = glyphMap[route.name] ?? 'help-outline';
          return <MaterialIcons name={glyph} size={size} color={color} />;
        },
        headerRight: () => <HeaderButtons navigation={navigation} />,
        tabBarActiveTintColor: fpColors.primary,
        tabBarInactiveTintColor: fpColors.outline,
        tabBarLabelStyle: {
          fontFamily: 'Inter_500Medium',
          fontSize: 11,
          letterSpacing: 0.2,
        },
        tabBarStyle: {
          backgroundColor: fpColors.surfaceContainerLowest,
          borderTopColor: fpColors.outlineVariant,
          height: 68,
        },
        tabBarItemStyle: {
          paddingVertical: 6,
        },
        headerStyle: {
          backgroundColor: fpColors.background,
        },
        headerTintColor: fpColors.onSurface,
        headerTitleStyle: {
          fontFamily: 'Inter_600SemiBold',
          fontSize: 16,
        },
      })}
    >
      <Tab.Screen
        name="Home"
        component={DashboardScreen}
        options={{ title: 'Dashboard', headerShown: false }}
      />
      <Tab.Screen name="Browse" component={BrowseScreen} options={{ title: 'Items', headerShown: false }} />
      {/* Sprint 8 follow-up — Verify tab dropped. Per-item verify now
          lives as a tap-chip on Browse cards. */}
      {/* FE #259 — Founsi AI chat tab.  Sits in the middle slot
          (sparkle icon) — same placement as Linear, Notion, Arc:
          AI agent gets visual centerpiece. */}
      <Tab.Screen
        name="Founsi"
        component={FounsiScreen}
        options={{
          title: 'Founsi',
          headerShown: false,
          tabBarTestID: 'tab-founsi',
        }}
      />
      {/* New "Share" tab — unified item-flow surface. PR2 mounts the
          stub; PR4 builds out the With-me / Out-from-me sections. */}
      <Tab.Screen
        name="ShareFlow"
        component={ShareFlowScreen}
        options={{
          title: 'Share',
          headerShown: false,
          // Stable hook for E2E: the bottom-tab "Share" label renders as a
          // non-clickable text node on web, so `getByText(/^Share/i)` times
          // out. journey1-audit-errors taps this via getByTestId instead.
          tabBarTestID: 'tab-share',
        }}
      />
      {/* The legacy "Community" tab keeps its route name (no
          deep-link breakage). FE-201 (PR20) flipped the user-facing
          label to "Groups" — the inner content surface is the
          GroupsHub the rest of the app links into. */}
      <Tab.Screen name="Community" component={CrewHubScreen} options={{ title: 'Circles', headerShown: false }} />
    </Tab.Navigator>
  );
};

// Main authenticated app navigator
const MainAppNavigator = () => {
  return (
    <Stack.Navigator>
      <Stack.Screen 
        name="MainTabs" 
        component={MainTabs} 
        options={{ headerShown: false }} 
      />
      
      {/* Profile screens — each owns its top bar */}
      <Stack.Screen
        name="Profile"
        component={ProfileScreen}
        options={{ title: 'Profile', headerShown: false }}
      />
      <Stack.Screen
        name="EditProfile"
        component={EditProfileScreen}
        options={{ title: 'Edit Profile', headerShown: false }}
      />
      <Stack.Screen
        name="ChangePassword"
        component={ChangePasswordScreen}
        options={{ title: 'Change Password', headerShown: false }}
      />
      <Stack.Screen
        name="ChangeEmail"
        component={ChangeEmailScreen}
        options={{ title: 'Change Email', headerShown: false }}
      />
      
      {/* Other screens */}
      <Stack.Screen
        name="Upload"
        component={ScanRouteComponent}
        options={{ title: 'Upload Item', headerShown: false }}
      />
      <Stack.Screen
        name="ItemDetail"
        component={ItemDetailScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name="Feedback"
        component={FeedbackScreen}
        options={{ title: 'Feedback', headerShown: false }}
      />

      {/* Sharing — Groups redesign stack routes (Sprint 1, Chunk 8) */}
      <Stack.Screen
        name="CreateGroup"
        component={CreateGroupScreen}
        options={{ title: 'New circle', headerShown: false, presentation: 'modal' }}
      />
      <Stack.Screen
        name="GroupDetail"
        component={GroupDetailScreen}
        options={{ title: 'Circle', headerShown: false }}
      />
      <Stack.Screen
        name="AcceptInvite"
        component={AcceptInviteScreen}
        options={{ title: 'Join circle', headerShown: false }}
      />
      <Stack.Screen
        name="SharingRequests"
        component={SharingRequestsScreen}
        options={{ title: 'Requests', headerShown: false }}
      />
      {/* Sprint 8 / FE-128 — Notification inbox. Reached via the
          bell icon in the top bar (HeaderButtons) and via deep
          link /notifications. */}
      <Stack.Screen
        name="Notifications"
        component={NotificationsScreen}
        options={{ title: 'Notifications', headerShown: false }}
      />
      {/* Sprint 8 Phase C — aggregate share + custody screens. */}
      <Stack.Screen
        name="MyShares"
        component={MySharesScreen}
        options={{ title: 'My shares', headerShown: false }}
      />
      <Stack.Screen
        name="MyCustody"
        component={MyCustodyScreen}
        options={{ title: 'Out on loan', headerShown: false }}
      />
      {/* Sprint 4 / Chunk 10B FE-10.7 — cross-group search */}
      <Stack.Screen
        name="CrossGroupSearch"
        component={CrossGroupSearchScreen}
        options={{ title: 'Search', headerShown: false }}
      />
      {/* Chunk 9.5 Phase B (FE-11.5.E) — read-only scene viewer.
          Tap a scene hero on the Browse Scenes layout to land here.
          Reuses ImageAnnotationStage with no-op handlers. */}
      <Stack.Screen
        name="SceneViewer"
        component={SceneViewerScreen}
        options={{ title: 'Snapshot', headerShown: false }}
      />
      {/* FE #124 — Scan History page (USJ.md J2.B4 v2).
          Lists every scan with status pill (LIVE / READY_TO_SAVE /
          FAILED / COMPLETED). Live-polls IN_PROGRESS rows. */}
      <Stack.Screen
        name="ScanHistory"
        component={ScanHistoryScreen}
        options={{ title: 'Scan history', headerShown: false }}
      />
    </Stack.Navigator>
  );
};

// Loading screen
const LoadingScreen = () => (
  <View
    style={{
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: fpColors.background,
    }}
  >
    <ActivityIndicator size="large" color={fpColors.primary} />
    <Text
      style={{
        marginTop: 12,
        color: fpColors.onSurfaceVariant,
        fontFamily: 'Inter_500Medium',
        fontSize: 13,
      }}
    >
      Loading…
    </Text>
  </View>
);

// Root navigator that handles authentication state
const RootNavigator = ({ navigationRef }: { navigationRef: any }) => {
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectAuthLoading);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const requiresVerification = useAppSelector(selectRequiresVerification);
  const user = useAppSelector(selectUser);
  const verificationToken = useAppSelector(selectVerificationToken);

  // Load user profile on app start
  useEffect(() => {
    console.log('🚀 AppNavigator: Loading user profile on app start');
    dispatch(loadUserProfile());
    // Drop legacy AsyncStorage mock-data key from the pre-Chunk-8 slice
    // (idempotent; no-op after first boot of v8 build), then load real
    // groups from /groups endpoint.
    dispatch(cleanupLegacyMockStorage());
    dispatch(loadGroups());
    // Hydrate per-item extras (lending rules, status badge, quantity)
    // from SecureStorage. See src/features/inventory/itemExtrasSlice.ts
    // for the mock-layer strategy.
    dispatch(hydrateItemExtras());
  }, [dispatch]);

  // Sprint 2.5 (FE-9.4.5) — pre-auth invite resume. When an
  // unauthenticated user lands on /invite/{token} via an email
  // link, the bootstrap helper in App.tsx stashes the token at
  // module load. By the time RootNavigator mounts, isLoading flips
  // false and the unauth stack mounts. React Navigation's deep-link
  // resolution may have raced the auth-state swap and landed the
  // user on Login instead of AcceptInvite. This hook recovers: when
  // unauth + a pending token exists + we're not already on
  // AcceptInvite, navigate there explicitly so the user sees the
  // proper "You've been invited to {Crew}" landing page with the
  // Continue with Google CTA.
  useEffect(() => {
    if (isAuthenticated) return;
    if (isLoading) return;
    const pending = getPendingInviteToken();
    if (!pending) return;
    if (!navigationRef.current) return;
    const currentRoute = navigationRef.current.getCurrentRoute?.();
    if (currentRoute?.name === 'AcceptInvite') return;
    console.log(
      '🔄 AppNavigator: pending invite token found pre-auth — routing to AcceptInvite',
    );
    setTimeout(() => {
      try {
        navigationRef.current?.navigate('AcceptInvite', { token: pending });
      } catch (err) {
        console.warn('AcceptInvite pre-auth nav failed', err);
      }
    }, 50);
  }, [isAuthenticated, isLoading]);

  // FE #199 — Post-auth deep-link replay. When the user landed on a
  // route inside the gated MainApp stack (/notifications, /groups/x,
  // /item/y, …) BEFORE auth resolved, the original URL was stashed
  // synchronously in App.tsx via bootstrapPostBootRedirectFromUrl().
  // Now that the MainApp stack is mounted, we replay the URL via
  // history.replaceState; React Navigation's linking config matches
  // and routes the user where they actually wanted to go.
  //
  // Invite tokens have their own resume path (the hook just below
  // this one) and aren't stashed here.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (isLoading) return;
    // Defer to the next tick so the MainApp stack has finished
    // mounting before the URL mutation propagates through React
    // Navigation's linking observer.
    const t = setTimeout(() => {
      const target = consumePostBootRedirect();
      if (target) {
        console.log(
          '🔗 AppNavigator: replaying post-boot deep-link target:',
          target,
        );
      }
    }, 0);
    return () => clearTimeout(t);
  }, [isAuthenticated, isLoading]);

  // Pending invite resume — Sprint 1 (Chunk 8). When a user lands on
  // /invite/{token} unauthenticated, signs in via the email path
  // (which navigates them away to LoginScreen → home), we pick up
  // the stashed token here on the *next* render that has both an
  // authenticated user AND a pending token, and re-route them back
  // into the AcceptInvite flow so the auto-accept effect can fire.
  useEffect(() => {
    if (!isAuthenticated) return;
    const pending = getPendingInviteToken();
    if (!pending) return;
    if (!navigationRef.current) return;
    // Avoid the resume loop if we're already on the AcceptInvite screen
    // (the screen itself handles auto-accept once user is authenticated).
    const currentRoute = navigationRef.current.getCurrentRoute?.();
    if (currentRoute?.name === 'AcceptInvite') return;
    console.log(
      '🔄 AppNavigator: pending invite token found post-auth — resuming AcceptInvite',
    );
    // Defer to next tick to ensure the navigation tree has settled.
    setTimeout(() => {
      try {
        navigationRef.current?.navigate('AcceptInvite', { token: pending });
      } catch (err) {
        console.warn('AcceptInvite resume nav failed', err);
        clearPendingInviteToken();
      }
    }, 0);
  }, [isAuthenticated, navigationRef]);

  // Clear requiresVerification if there's no verification token (handles refresh scenarios)
  useEffect(() => {
    if (requiresVerification && !verificationToken && !isLoading && navigationRef.current) {
      console.log('🔄 AppNavigator: requiresVerification is true but no token found, clearing verification state and forcing navigation');
      dispatch(logoutUser({ callBackend: false }));
      
      // Force navigation to login screen
      setTimeout(() => {
        if (navigationRef.current) {
          navigationRef.current.dispatch(
            CommonActions.reset({
              index: 0,
              routes: [{ name: 'Login' }],
            })
          );
        }
      }, 100);
    }
  }, [requiresVerification, verificationToken, isLoading, dispatch]);

  // Show loading screen during initial auth check
  if (isLoading) {
    return <LoadingScreen />;
  }

  console.log('🧭 RootNavigator: Current state:', {
    isAuthenticated,
    requiresVerification,
    hasUser: !!user,
    isVerified: user?.is_verified,
    hasVerificationToken: !!verificationToken
  });

  // If requiresVerification is true but no verification token, redirect to login
  // This handles page refresh scenarios
  const shouldShowVerification = requiresVerification && verificationToken;

  return (
    <Stack.Navigator screenOptions={{ headerShown: false }}>
      {isAuthenticated && !requiresVerification ? (
        // User is fully authenticated and verified - show main app
        <Stack.Screen name="MainApp" component={MainAppNavigator} />
      ) : shouldShowVerification ? (
        // User needs email verification AND has valid token - show verification screen
        <>
          <Stack.Screen 
            name="EmailVerification" 
            component={EmailVerificationScreen}
            options={{ title: 'Verify Email' }}
          />
          <Stack.Screen 
            name="Login" 
            component={LoginScreen}
            options={{ title: 'Sign In' }}
          />
          <Stack.Screen 
            name="Register" 
            component={RegisterScreen}
            options={{ title: 'Create Account' }}
          />
          <Stack.Screen 
            name="ForgotPassword" 
            component={ForgotPasswordScreen}
            options={{ title: 'Forgot Password' }}
          />
          <Stack.Screen 
            name="ResetPassword" 
            component={ResetPasswordScreen}
            options={{ title: 'Reset Password' }}
          />
        </>
      ) : (
        // User is not authenticated OR needs verification but no token - show auth screens
        <>
          <Stack.Screen
            name="Login"
            component={LoginScreen}
            options={{ title: 'Sign In' }}
          />
          <Stack.Screen
            name="Register"
            component={RegisterScreen}
            options={{ title: 'Create Account' }}
          />
          <Stack.Screen
            name="EmailVerification"
            component={EmailVerificationScreen}
            options={{ title: 'Verify Email' }}
          />
          <Stack.Screen
            name="ForgotPassword"
            component={ForgotPasswordScreen}
            options={{ title: 'Forgot Password' }}
          />
          <Stack.Screen
            name="ResetPassword"
            component={ResetPasswordScreen}
            options={{ title: 'Reset Password' }}
          />
          {/* Sprint 2.5 (FE-9.4.5) — also register AcceptInvite in the
              unauth stack so a user clicking an invite email link while
              signed-out actually lands on the proper "You've been
              invited to {Crew}" landing page (state A in the screen's
              auth-aware render) instead of being bounced to the generic
              Login screen with no context. The screen's internal
              auto-accept effect only runs when isAuthenticated, so
              showing it pre-auth is safe — it just renders the Google
              sign-in CTA + email-signup link. */}
          <Stack.Screen
            name="AcceptInvite"
            component={AcceptInviteScreen}
            options={{ title: 'Join circle', headerShown: false }}
          />
        </>
      )}

      {/* Share claim screen - available to all users */}
      <Stack.Screen
        name="ShareClaim"
        component={ShareClaimScreen}
        options={{ headerShown: false }}
      />
    </Stack.Navigator>
  );
};

// Main App Navigator component
export const AppNavigator = () => {
  const navigationRef = useRef<any>(null);
  
  return (
    <NavigationContainer 
      ref={navigationRef}
      linking={linking}
      onStateChange={(state: any) => {
        const currentRoute = state?.routes?.[state?.index || 0]?.name;
        console.log('🧭 Navigation state changed:', {
          currentRoute,
          timestamp: new Date().toISOString()
        });
        
        // Track navigation with Clarity
        if (currentRoute) {
          clarityHelpers.trackNavigation(currentRoute);
        }
      }}
      onReady={() => {
        console.log('🧭 Navigation ready');
      }}
    >
      <FeedbackProvider>
        <RootNavigator navigationRef={navigationRef} />
        <ShareModal />
        {/* Search-UX §4.1 — ⌘K / Ctrl+K universal command palette.
            Mounted at the navigation root so the keyboard shortcut
            is global.  Renders null when closed; opens on the
            keyboard shortcut and closes on Esc or scrim tap. */}
        <CommandPalette />
      </FeedbackProvider>
    </NavigationContainer>
  );
};